import { skipToken } from '@reduxjs/toolkit/query';
import { capitalizeString, DateFormats, formatDate } from '@strata/helper-functions';
import { Grid, SecondaryData } from '@strata/react-grid';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { AppRoutes } from 'services/routes';
import { useGetAllSuppliersQuery } from 'store/api/referenceApi';
import { useGetReferralCreationDecisionsForTreatmentQuery } from 'store/api/treatmentApi';

const initialSortProperties = [{ id: 'decisionTime', desc: true }];

const formatCode = (code) => capitalizeString(code?.replaceAll('_', ' '));

const formatSupplierLabel = (supplierMap, npi) => {
    if (!npi) {
        return null;
    }
    const name = supplierMap?.[npi]?.name;
    return name ? `${name} (${npi})` : npi;
};

const columns = (supplierMap) => [
    {
        accessorKey: 'decisionTime',
        header: 'Date/Time',
        size: 40,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorFn: ({ outcome }) => formatCode(outcome),
        id: 'outcome',
        header: 'Outcome',
        size: 30,
    },
    {
        accessorFn: ({ reasons }) => reasons.map(formatCode).join(', '),
        id: 'reasons',
        header: 'Reasons',
        size: 80,
    },
    {
        accessorKey: 'opportunitySource',
        header: 'Opportunity Source',
        size: 40,
        cell: ({ getValue, row }) => {
            const source = getValue();
            const supplierLabel = formatSupplierLabel(
                supplierMap,
                row.original.billingParentPharmacyNpi,
            );
            return (
                <SecondaryData
                    data={source}
                    customTooltipContent={
                        supplierLabel ? (
                            <>
                                <div>{source}</div>
                                <div>Supplier: {supplierLabel}</div>
                            </>
                        ) : undefined
                    }
                />
            );
        },
    },
    {
        accessorKey: 'sourceEvidenceId',
        header: 'Source Evidence ID',
        size: 60,
    },
    {
        accessorKey: 'createdReferralFriendlyId',
        header: 'Created Referral',
        size: 60,
    },
];

export const ReferralCreationDecisionsTab = ({ payerId, treatmentId, currentLocation }) => {
    const navigate = useNavigate();
    const { data: decisions = [], isFetching } = useGetReferralCreationDecisionsForTreatmentQuery(
        payerId && treatmentId ? { payerId, treatmentId } : skipToken,
    );
    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};

    const handleRowClick = ({ createdReferralId }) => {
        if (!createdReferralId) {
            return;
        }
        navigate(AppRoutes.referralDetails(payerId, createdReferralId), {
            state: { backToDestination: currentLocation },
        });
    };

    return (
        <Grid
            enableSorting
            data={decisions}
            isLoading={isFetching}
            sortProperties={initialSortProperties}
            columns={columns(supplierMap)}
            onRowClick={handleRowClick}
        />
    );
};

ReferralCreationDecisionsTab.propTypes = {
    payerId: PropTypes.string.isRequired,
    treatmentId: PropTypes.string.isRequired,
    currentLocation: PropTypes.shape({ text: PropTypes.string }).isRequired,
};
